/**
 * Centro de Control Logístico IA (/control-tower) — pantalla única de solo
 * lectura, estilo torre de control, para un monitor de la sala de operaciones.
 *
 * Compone piezas existentes (KpiBand, TorreMap, DriverBoard, AlertsPanel de la
 * Torre de control) + bloques nuevos (semáforo, SLA, embudo, Calidad IA) sobre
 * `useControlTowerIA`. Siempre "hoy" en la TZ del operador; auto-refresca solo.
 * Scoped al logisticCompanyId del usuario de display logueado.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioTower, RefreshCw, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { RelativeTime } from '../../components/ui/RelativeTime';
import useAuth from '../../hooks/useAuth';
import useTheme from '../../hooks/useTheme';
import { getOperationTimezone } from '../../utils/timezone';
import { localDateStr } from '../../utils/formatters';
import TorreMap from '../adminView/torreControl/TorreMap';
import DriverBoard from '../adminView/torreControl/components/DriverBoard';
import useControlTowerIA from './useControlTowerIA';
import StatusBanner from './components/StatusBanner';
import StatusKpis from './components/StatusKpis';
import SlaCards from './components/SlaCards';
import ForecastCard from './components/ForecastCard';

// Capas del mapa: SOLO paquetes no entregados (pendientes + fallidos). Sin
// vehículos/repartidores (conductores:false) y sin entregados: el foco es
// únicamente lo que falta por entregar.
const MAP_LAYERS = {
  conductores: false,
  stops: true,
  pendientes: true,
  entregados: false,
  fallidos: true,
};

export default function ControlTowerIA() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const timezone = useMemo(() => getOperationTimezone(user), [user]);
  const today = useMemo(() => localDateStr(new Date(), timezone), [timezone]);

  const ct = useControlTowerIA({ date: today });

  if (!ct.logisticCompanyId) {
    return (
      <div className="p-4 md:p-6">
        <Card>
          <EmptyState
            icon={<RadioTower />}
            title={t('Sin empresa logística asociada')}
            description={t('Este perfil de display no tiene una empresa logística asociada, así que no hay operación que monitorear.')}
          />
        </Card>
      </div>
    );
  }

  const activeDrivers = ct.gpsLiveDrivers;
  const totalDrivers = ct.activeDriversCount ?? ct.totalDriversToday;

  return (
    <div className="space-y-4 p-3 md:p-5">
      {/* Barra de estado en vivo */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5" aria-hidden>
            <span className="absolute inline-flex h-full w-full rounded-full bg-status-success opacity-70 motion-safe:animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-status-success" />
          </span>
          <span className="text-sm font-medium text-fg">{t('Operación en vivo')}</span>
          {!ct.gpsConnected && (
            <span className="inline-flex items-center gap-1 text-[11px] text-status-warning">
              <AlertTriangle className="h-3 w-3" /> {t('Sin conexión GPS en vivo.')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {ct.lastUpdated && (
            <span className="hidden text-[11px] text-fg-muted sm:inline">
              {t('Actualizado')}{' '}
              <RelativeTime value={ct.lastUpdated} live t={t} />
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            iconLeft={<RefreshCw className={cn('h-3.5 w-3.5', ct.refreshing && 'motion-safe:animate-spin')} />}
            onClick={ct.refetch}
            disabled={ct.refreshing}
          >
            <span className="hidden sm:inline">{t('Refrescar')}</span>
          </Button>
        </div>
      </div>

      {ct.error && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-status-error/30 bg-status-error-soft px-4 py-2.5 text-sm text-status-error"
          role="alert"
        >
          <span>{ct.error}</span>
          <button
            type="button"
            onClick={ct.refetch}
            className="shrink-0 font-medium underline underline-offset-2 hover:no-underline"
          >
            {t('Reintentar')}
          </button>
        </div>
      )}

      {/* Estado General (semáforo) */}
      <StatusBanner status={ct.status} loading={ct.loading} />

      {/* KPIs por estado */}
      <StatusKpis
        statusCounts={ct.funnelCounts}
        kpis={ct.kpis}
        activeDrivers={activeDrivers}
        totalDrivers={totalDrivers}
        loading={ct.loading}
      />

      {/* Proyección del día + SLA + riesgo */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <ForecastCard forecast={ct.forecast} actual={ct.kpis?.total || 0} loading={ct.loading} />
        <SlaCards op={ct.op} kpis={ct.kpis} atRisk={ct.atRisk} loading={ct.loading} />
      </div>

      {/* Mapa en vivo no-entregados + Repartidores en operación */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card padding="none" className="overflow-hidden xl:col-span-2">
          <div className="relative w-full" style={{ height: 'clamp(360px, 50vh, 620px)' }}>
            <TorreMap isDark={isDark} rutas={ct.rutas || []} layers={MAP_LAYERS} />

            {/* Indicador "en vivo" */}
            <div className="absolute right-3 top-3 z-[400]">
              <div className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-bg px-3 text-xs font-medium shadow-card">
                <span className="relative flex h-2 w-2" aria-hidden>
                  <span className="absolute inline-flex h-full w-full rounded-full bg-status-success opacity-70 motion-safe:animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-status-success" />
                </span>
                <span className="text-fg">{t('No entregados')}</span>
              </div>
            </div>

            {/* Leyenda */}
            <div className="absolute bottom-3 left-3 z-[400]">
              <div className="rounded-lg border border-border bg-bg/95 px-3 py-2 shadow-card">
                <div className="grid grid-cols-1 gap-y-1 text-[11px] text-fg-muted">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: '#F59E0B' }} />
                    {t('Pendiente')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: '#DC2626' }} />
                    {t('No entregada')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <DriverBoard rutas={ct.rutas} loading={ct.loading} orderBy="enRutaDesc" />
      </div>
    </div>
  );
}
